import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';
import { isMobileBreakpoint } from '@theme/utilities';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement} [liveRegion] - The live region for cart announcements when dialog is open.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {AbortController | null} */
  #historyAbortController = null;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);

    if (history.state?.cartDrawerOpen) {
      history.replaceState(null, '');
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    this.#historyAbortController?.abort();
  }

  #handleHistoryOpen = () => {
    if (!isMobileBreakpoint()) return;

    if (!history.state?.cartDrawerOpen) {
      history.pushState({ cartDrawerOpen: true }, '');
    }

    this.#historyAbortController = new AbortController();
    window.addEventListener('popstate', this.#handlePopState, { signal: this.#historyAbortController.signal });
  };

  #handleHistoryClose = () => {
    this.#historyAbortController?.abort();
    if (history.state?.cartDrawerOpen) {
      history.back();
    }
  };

  #handlePopState = async () => {
    if (this.refs.dialog?.open) {
      this.refs.dialog.style.setProperty('--dialog-drawer-closing-animation', 'none');
      await this.closeDialog();
      this.refs.dialog.style.removeProperty('--dialog-drawer-closing-animation');
    }
  };

  /**
   * Handles cart add events - opens drawer if auto-open and announces count when open.
   * @param {CustomEvent<{ resource?: { item_count?: number } }>} event
   */
  #handleCartAdd = (event) => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }

    this.#announceCartCount(event.detail.resource?.item_count);
  };

  /**
   * Announces cart count to screen readers when dialog is open.
   * @param {number | undefined} cartCount
   */
  #announceCartCount(cartCount) {
    const liveRegion = /** @type {HTMLElement | undefined} */ (this.refs.liveRegion);
    if (!this.refs.dialog?.open || !liveRegion || cartCount === undefined) return;

    liveRegion.textContent = `${Theme.translations.cart_count}: ${cartCount}`;
  }

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
(function () {
  const FREE_SHIPPING_THRESHOLD = 5000; // 50,00 € w centach

  function getLang() {
    const lang = document.documentElement.lang || 'nl';
    return lang.slice(0, 2);
  }

  function formatMoney(cents) {
    const lang = getLang();
    const locale = lang === 'de' ? 'de-DE' : 'nl-NL';

    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: 'EUR'
    }).format(cents / 100);
  }

  function getMessage(cartTotal) {
    const lang = getLang();
    const remaining = FREE_SHIPPING_THRESHOLD - cartTotal;

    if (remaining <= 0) {
      return lang === 'de'
        ? 'Du hast kostenlosen Versand erreicht!'
        : 'Je hebt gratis verzending bereikt!';
    }

    return lang === 'de'
      ? 'Noch ' + formatMoney(remaining) + ' bis zum kostenlosen Versand.'
      : 'Nog ' + formatMoney(remaining) + ' tot gratis verzending.';
  }

  function getProgress(cartTotal) {
    return Math.min(100, Math.round((cartTotal / FREE_SHIPPING_THRESHOLD) * 100));
  }

  async function updateFreeShippingBar() {
    try {
      const root = window.Shopify && Shopify.routes ? Shopify.routes.root : '/';
      const response = await fetch(root + 'cart.js');
      const cart = await response.json();

      let bar = document.querySelector('#free-shipping-bar-custom');

      if (!bar) {
        bar = document.createElement('div');
        bar.id = 'free-shipping-bar-custom';

        bar.innerHTML = `
          <div class="free-shipping-message" style="font-weight:600;margin-bottom:8px;font-size:14px;"></div>
          <div style="width:100%;height:8px;background:#e5e5e5;border-radius:50px;overflow:hidden;">
            <div class="free-shipping-progress" style="height:8px;width:0%;background:#111;border-radius:50px;transition:width 0.3s ease;"></div>
          </div>
        `;

        bar.style.margin = '12px 0';
        bar.style.padding = '12px';
        bar.style.border = '1px solid #dddddd';
        bar.style.borderRadius = '8px';
        bar.style.background = '#ffffff';

        const checkoutButton =
          document.querySelector('[name="checkout"]') ||
          document.querySelector('.cart__checkout-button') ||
          document.querySelector('button[type="submit"]') ||
          document.querySelector('a[href="/checkout"]');

        if (checkoutButton && checkoutButton.parentNode) {
          checkoutButton.parentNode.insertBefore(bar, checkoutButton);
        }
      }

      const messageEl = bar.querySelector('.free-shipping-message');
      const progressEl = bar.querySelector('.free-shipping-progress');

      if (messageEl) {
        messageEl.textContent = getMessage(cart.total_price);
      }

      if (progressEl) {
        progressEl.style.width = getProgress(cart.total_price) + '%';
      }

    } catch (error) {
      console.warn('Free shipping bar error:', error);
    }
  }

  document.addEventListener('DOMContentLoaded', updateFreeShippingBar);
  document.addEventListener('cart:updated', updateFreeShippingBar);
  document.addEventListener('cart:change', updateFreeShippingBar);
  document.addEventListener('shopify:section:load', updateFreeShippingBar);

  const oldFetch = window.fetch;

  window.fetch = async function () {
    const response = await oldFetch.apply(this, arguments);

    const url = typeof arguments[0] === 'string'
      ? arguments[0]
      : arguments[0]?.url;

    if (url && url.includes('/cart')) {
      setTimeout(updateFreeShippingBar, 400);
    }

    return response;
  };

  setTimeout(updateFreeShippingBar, 500);
  setTimeout(updateFreeShippingBar, 1500);
})();